import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Share, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogBody,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

export const InstallPWAButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Check if already installed/standalone
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standalone);

    const handler = (e: Event) => {
      // Prevent the default browser install prompt
      e.preventDefault();
      // Store the event so we can trigger it later with our custom button
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  // Don't show if already installed
  if (isStandalone) return null;

  // Show different UI for iOS vs other browsers
  if (isIOS) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-12 w-12 p-0 hover:bg-muted"
            title="Install app"
          >
            <Download className="h-5 w-5" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Install Vivport</DialogTitle>
            <DialogDescription>
              Add this app to your home screen for quick access
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-info-subtle">
                <span className="text-sm font-medium">1</span>
              </div>
              <div className="flex items-center gap-2">
                <span>In address bar, tap the share button</span>
                <Share className="h-4 w-4" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-info-subtle">
                <span className="text-sm font-medium">2</span>
              </div>
              <div className="flex items-center gap-2">
                <span>Tap "Add to Home Screen"</span>
                <Plus className="h-4 w-4" />
              </div>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    );
  }

  // Show Chrome/Edge install button if available
  if (deferredPrompt) {
    return (
      <Button
        onClick={handleInstallClick}
        variant="ghost"
        size="sm"
        className="h-12 w-12 p-0 hover:bg-muted"
        title="Install app"
      >
        <Download className="h-5 w-5" />
      </Button>
    );
  }

  // Don't show anything if PWA not available
  return null;
};
